(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return;

  const DURATION = 300;
  const DISTANCE = 32;

  const REGIONS = [
    '.page-head',
    '[data-collection-nav]',
    '.collection-toolbar',
    '#ProductGrid',
    '.load-more',
    '.pagination',
    '.collection-empty'
  ];

  let navLinks = [];
  let currentUrl = location.href;
  let inFlight = null;
  let isNavigating = false;

  function getNavLinks() {
    const nav = document.querySelector('[data-collection-nav]');
    if (!nav) return [];
    return Array.from(nav.querySelectorAll('a')).map(a => a.href);
  }

  function getDirection(fromUrl, toUrl) {
    const fromIdx = navLinks.findIndex(u => fromUrl.startsWith(u.split('?')[0]));
    const toIdx = navLinks.findIndex(u => toUrl.startsWith(u.split('?')[0]));
    if (fromIdx === -1 || toIdx === -1) return 1;
    return toIdx >= fromIdx ? 1 : -1;
  }

  // Find the wrapper that contains all swappable regions
  function getContainer() {
    return document.querySelector('main') || document.body;
  }

  async function navigate(toUrl, pushState = true) {
    if (toUrl === currentUrl) return;
    if (inFlight) inFlight.abort();

    const controller = new AbortController();
    inFlight = controller;
    isNavigating = true;

    const direction = getDirection(currentUrl, toUrl);

    // Update nav active state immediately
    document.querySelectorAll('[data-collection-nav] a').forEach(a => {
      if (a.href === toUrl || toUrl.startsWith(a.href.split('?')[0])) {
        a.setAttribute('aria-current', 'page');
      } else {
        a.removeAttribute('aria-current');
      }
    });

    // Lock container height to prevent footer jump
    const container = getContainer();
    const lockedHeight = container.offsetHeight;
    container.style.minHeight = lockedHeight + 'px';

    // Collect current live elements
    const liveEls = REGIONS.map(sel => document.querySelector(sel)).filter(Boolean);

    // Fetch destination while current content is still fully visible
    let fetchedDoc;
    try {
      const res = await fetch(toUrl, { signal: controller.signal });
      if (!res.ok) throw new Error('fetch failed');
      const html = await res.text();
      fetchedDoc = new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      container.style.minHeight = '';
      isNavigating = false;
      inFlight = null;
      if (e.name !== 'AbortError') location.href = toUrl;
      return;
    }

    if (controller.signal.aborted) {
      container.style.minHeight = '';
      isNavigating = false;
      return;
    }

    // Start fading out current content (do not await — overlap with swap)
    const outAnimations = liveEls.map(el => {
      if (!el) return null;
      return el.animate([
        { opacity: 1, transform: 'translateX(0)' },
        { opacity: 0, transform: `translateX(${-direction * DISTANCE}px)` }
      ], { duration: DURATION * 0.6, easing: 'ease-in', fill: 'forwards' }).finished;
    });

    // Wait for fade-out to complete
    await Promise.all(outAnimations);

    if (controller.signal.aborted) {
      container.style.minHeight = '';
      isNavigating = false;
      return;
    }

    // Swap regions
    REGIONS.forEach(sel => {
      const current = document.querySelector(sel);
      const next = fetchedDoc.querySelector(sel);
      if (current && next) {
        current.replaceWith(next.cloneNode(true));
      } else if (current && !next) {
        current.remove();
      } else if (!current && next) {
        const wrapper = document.querySelector('.section-shell--tight') || container;
        if (wrapper) wrapper.appendChild(next.cloneNode(true));
      }
    });

    // Update title and history
    const newTitle = fetchedDoc.querySelector('title');
    if (newTitle) document.title = newTitle.textContent;

    if (pushState) {
      history.pushState({ collectionUrl: toUrl }, document.title, toUrl);
    }

    currentUrl = toUrl;
    navLinks = getNavLinks();

    // Release height lock
    container.style.minHeight = '';

    // Animate new elements in
    const newEls = REGIONS.map(sel => document.querySelector(sel)).filter(Boolean);
    await Promise.all(newEls.map(el => {
      if (!el) return Promise.resolve();
      return el.animate([
        { opacity: 0, transform: `translateX(${direction * DISTANCE}px)` },
        { opacity: 1, transform: 'translateX(0)' }
      ], { duration: DURATION * 0.7, easing: 'ease-out', fill: 'forwards' }).finished;
    }));

    // Scroll to keep nav visible without jumping to top
    const nav = document.querySelector('[data-collection-nav]');
    if (nav) {
      const navTop = nav.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY > navTop) {
        window.scrollTo({ top: navTop - 16, behavior: 'smooth' });
      }
    }

    isNavigating = false;
    inFlight = null;
  }

  // Intercept collection-nav clicks only
  document.addEventListener('click', e => {
    const nav = e.target.closest('[data-collection-nav]');
    if (!nav) return;
    const link = e.target.closest('a');
    if (!link) return;
    e.preventDefault();
    navigate(link.href);
  });

  // Back/Forward
  window.addEventListener('popstate', e => {
    const url = e.state?.collectionUrl || location.href;
    navigate(url, false);
  });

  navLinks = getNavLinks();
})();
